/**
 * Hands a file to the phone: its share sheet when the browser can share files (save to Drive,
 * send to a doctor on WhatsApp), else an ordinary download.
 *
 * The share sheet comes first because it is where a phone keeps "save" as well as "send"; a bare
 * download on Android lands in a folder nobody opens. `canShare` is asked with the file itself —
 * a browser that shares text but not files answers no, and would otherwise throw on `share`.
 */
export type ShareOutcome = "shared" | "downloaded" | "cancelled";

export async function shareOrDownload(name: string, text: string, type: string): Promise<ShareOutcome> {
  const blob = new Blob([text], { type });
  const file = typeof File === "function" ? new File([blob], name, { type }) : null;
  const nav = typeof navigator !== "undefined" ? navigator : null;

  if (file && nav?.share && nav.canShare?.({ files: [file] })) {
    try {
      await nav.share({ files: [file], title: name });
      return "shared";
    } catch (e) {
      // Closing the sheet is a choice, not a failure; anything else falls through to a download.
      if (e instanceof DOMException && e.name === "AbortError") return "cancelled";
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoked late: some browsers start reading the URL after the click has returned.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return "downloaded";
}
